import { useState, useCallback, useEffect } from 'react';
import type {
  ReplyParams,
  CommentMention
} from '../../containers/CommentList/CommentList.types';

/**
 * Unified hook for managing form state - supports both single and multiple forms
 * Consolidates functionality from useCommentFormState and useCommentReply
 * Follows DRY principle by eliminating duplicate form management logic
 */
export const useFormManager = (
  // Global handlers for multi-form scenarios
  handleFormSubmit?: (
    params: ReplyParams | { text: string; commentMentions?: CommentMention[] }
  ) => void,
  handleReplySubmit?: (params: ReplyParams) => void
) => {
  // Multi-form state management
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formMentions, setFormMentions] = useState<
    Record<string, CommentMention[]>
  >({});
  const [formConfig, setFormConfig] = useState<
    Record<
      string,
      {
        isControlled: boolean;
        externalValue?: string;
        externalOnChange?: (
          event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => void;
      }
    >
  >({});

  // Multi-form API (replaces useCommentReply functionality)
  const submitForm = useCallback(
    async (
      parentCommentId: string,
      text: string,
      mentions?: CommentMention[],
      attachments?: File[]
    ) => {
      const replyId = `reply-${Date.now()}`;
      const params: ReplyParams = {
        id: replyId,
        text,
        commentMentions: mentions,
        parentCommentId,
        ...(attachments?.length ? { attachments } : {})
      };

      if (handleReplySubmit) {
        handleReplySubmit(params);
      } else if (handleFormSubmit) {
        handleFormSubmit(params);
      }

      setFormValues(prev => ({ ...prev, [parentCommentId]: '' }));
      setFormMentions(prev => ({ ...prev, [parentCommentId]: [] }));
    },
    [handleReplySubmit, handleFormSubmit]
  );

  const changeFormValue = useCallback(
    (formId: string, value: string) => {
      const config = formConfig[formId];
      if (config?.isControlled && config.externalOnChange) {
        // Create synthetic event for controlled forms
        const syntheticEvent = {
          target: { value }
        } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
        config.externalOnChange(syntheticEvent);
      } else {
        setFormValues(prev => ({ ...prev, [formId]: value }));
      }
    },
    [formConfig]
  );

  const handleFormValueChange = useCallback(
    (
      formId: string,
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const config = formConfig[formId];
      if (config?.isControlled && config.externalOnChange) {
        config.externalOnChange(event);
      } else {
        setFormValues(prev => ({ ...prev, [formId]: event.target.value }));
      }
    },
    [formConfig]
  );

  const setMentionsForForm = useCallback(
    (formId: string, mentions: CommentMention[]) => {
      setFormMentions(prev => ({ ...prev, [formId]: mentions }));
    },
    []
  );

  const getFormValue = useCallback(
    (formId: string) => {
      const config = formConfig[formId];
      if (config?.isControlled) {
        return config.externalValue ?? '';
      }
      return formValues[formId] || '';
    },
    [formValues, formConfig]
  );

  const getFormMentions = useCallback(
    (formId: string) => {
      return formMentions[formId] || [];
    },
    [formMentions]
  );

  const clearForm = useCallback(
    (formId: string) => {
      const config = formConfig[formId];
      if (!config?.isControlled) {
        setFormValues(prev => ({ ...prev, [formId]: '' }));
      }
      setFormMentions(prev => ({ ...prev, [formId]: [] }));
    },
    [formConfig]
  );

  const resetForm = useCallback(
    (formId: string) => {
      clearForm(formId);
    },
    [clearForm]
  );

  const configureForm = useCallback(
    (
      formId: string,
      isControlled: boolean,
      externalValue?: string,
      externalOnChange?: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => void
    ) => {
      setFormConfig(prev => ({
        ...prev,
        [formId]: {
          isControlled,
          externalValue,
          externalOnChange
        }
      }));
    },
    []
  );

  return {
    // Multi-form API (useCommentReply replacement)
    formValues,
    submitForm,
    changeFormValue: changeFormValue,
    getFormValue,
    setMentionsForForm,
    getFormMentions,
    clearForm,
    resetForm,
    configureForm,
    handleFormValueChange
  };
};

/**
 * Single-form convenience hook that uses useFormManager under the hood
 * Provides the same API as the old useCommentFormState for backward compatibility
 */
export const useSingleForm = (
  initialValue: string = '',
  isControlled: boolean = false,
  externalValue?: string,
  externalOnChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
) => {
  const {
    configureForm,
    getFormValue,
    getFormMentions,
    handleFormValueChange,
    setMentionsForForm,
    clearForm,
    resetForm
  } = useFormManager();

  const SINGLE_FORM_ID = 'single-form';

  // Configure the single form on mount and when props change
  useEffect(() => {
    configureForm(
      SINGLE_FORM_ID,
      isControlled,
      externalValue,
      externalOnChange
    );
  }, [configureForm, isControlled, externalValue, externalOnChange]);

  // Initialize value if not set and not controlled
  useEffect(() => {
    const currentValue = getFormValue(SINGLE_FORM_ID);
    if (!currentValue && initialValue && !isControlled) {
      // Use changeFormValue to set initial value
      const syntheticEvent = {
        target: { value: initialValue }
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
      handleFormValueChange(SINGLE_FORM_ID, syntheticEvent);
    }
  }, [initialValue, isControlled, getFormValue, handleFormValueChange]);

  const currentValue = getFormValue(SINGLE_FORM_ID);
  const currentMentions = getFormMentions(SINGLE_FORM_ID);

  const handleValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleFormValueChange(SINGLE_FORM_ID, event);
    },
    [handleFormValueChange]
  );

  const handleMentionsChange = useCallback(
    (newMentions: CommentMention[]) => {
      setMentionsForForm(SINGLE_FORM_ID, newMentions);
    },
    [setMentionsForForm]
  );

  const clearSingleForm = useCallback(() => {
    clearForm(SINGLE_FORM_ID);
  }, [clearForm]);

  const resetSingleForm = useCallback(() => {
    resetForm(SINGLE_FORM_ID);
  }, [resetForm]);

  return {
    currentValue: isControlled ? externalValue ?? '' : currentValue,
    mentions: currentMentions,
    handleValueChange,
    handleMentionsChange,
    clearValue: clearSingleForm,
    resetForm: resetSingleForm
  };
};
